import { useState, useEffect, useCallback } from 'react'
import { carService } from '../services/carService'

export const useCarController = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [currentDirection, setCurrentDirection] = useState('STOP')
  const [currentSpeed, setCurrentSpeed] = useState(0)
  const [isMoving, setIsMoving] = useState(false)
  const [deviceStatus, setDeviceStatus] = useState(null)
  const [connectionError, setConnectionError] = useState(null)
  const [connectedDeviceIP, setConnectedDeviceIP] = useState(null)

  // Load saved connection on mount
  useEffect(() => {
    const savedDeviceIP = localStorage.getItem('ezcontrol_connected_device')
    if (savedDeviceIP) {
      console.log('Found saved device IP:', savedDeviceIP)
      setConnectedDeviceIP(savedDeviceIP)
      // Attempt to reconnect only once on mount
      connectToDevice(savedDeviceIP).catch(error => {
        console.log('Auto-reconnection failed, device may be offline:', error.message)
        // Don't remove from localStorage on auto-reconnect failure
        // Let user manually disconnect if needed
      })
    }
  }, []) // Empty dependency array to run only once on mount

  // Connect to a device
  const connectToDevice = useCallback(async (deviceIP) => {
    try {
      setConnectionError(null)
      const status = await carService.connect(deviceIP)
      setIsConnected(true)
      setConnectedDeviceIP(deviceIP)
      setDeviceStatus(status)
      
      // Save connection to localStorage
      localStorage.setItem('ezcontrol_connected_device', deviceIP)
      
      // Update local state from device status
      if (status) {
        setCurrentDirection(status.direction || 'STOP')
        setCurrentSpeed(status.speed || 0)
        setIsMoving(status.isMoving || false)
      }
      
      console.log('Connected to device:', deviceIP)
    } catch (error) {
      setConnectionError(error.message)
      setIsConnected(false)
      setConnectedDeviceIP(null)
      localStorage.removeItem('ezcontrol_connected_device')
      console.error('Failed to connect:', error)
    }
  }, [])

  // Disconnect from device
  const disconnectFromDevice = useCallback(() => {
    carService.disconnect()
    setIsConnected(false)
    setConnectedDeviceIP(null)
    setCurrentDirection('STOP')
    setCurrentSpeed(0)
    setIsMoving(false)
    setDeviceStatus(null)
    setConnectionError(null)
    localStorage.removeItem('ezcontrol_connected_device')
  }, [])

  // Send car command - Updated for NodeMCU.ino API
  const sendCarCommand = useCallback(async (command, speed = null) => {
    if (!isConnected) {
      console.warn('Not connected to device')
      return
    }

    try {
      let response

      // Handle car movement commands
      switch (command) {
        case 'car_forward':
          response = await carService.moveForward(speed)
          setCurrentDirection('FORWARD')
          setIsMoving(true)
          break
        case 'car_backward':
          response = await carService.moveBackward(speed)
          setCurrentDirection('BACKWARD')
          setIsMoving(true)
          break
        case 'car_left':
          response = await carService.turnLeft(speed)
          setCurrentDirection('LEFT')
          setIsMoving(true)
          break
        case 'car_right':
          response = await carService.turnRight(speed)
          setCurrentDirection('RIGHT')
          setIsMoving(true)
          break
        case 'car_left_forward':
          response = await carService.moveLeftForward(speed)
          setCurrentDirection('LEFT-FORWARD')
          setIsMoving(true)
          break
        case 'car_right_forward':
          response = await carService.moveRightForward(speed)
          setCurrentDirection('RIGHT-FORWARD')
          setIsMoving(true)
          break
        case 'car_left_backward':
          response = await carService.moveLeftBackward(speed)
          setCurrentDirection('LEFT-BACKWARD')
          setIsMoving(true)
          break
        case 'car_right_backward':
          response = await carService.moveRightBackward(speed)
          setCurrentDirection('RIGHT-BACKWARD')
          setIsMoving(true)
          break
        case 'car_stop':
          response = await carService.stop()
          setCurrentDirection('STOP')
          setIsMoving(false)
          break
        case 'car_speed':
          response = await carService.setSpeed(speed)
          if (speed !== null) {
            setCurrentSpeed(speed)
          }
          break
        default:
          console.warn('Unknown command:', command)
          return
      }

      return response
    } catch (error) {
      console.error('Failed to send command:', error)
      // If connection fails, disconnect
      if (error.message.includes('connection') || error.message.includes('Failed to send command')) {
        disconnectFromDevice()
      }
    }
  }, [isConnected, disconnectFromDevice])

  // Send GPIO command - Updated for NodeMCU.ino API
  const sendGPIOCommand = useCallback(async (command, params = {}) => {
    if (!isConnected) {
      console.warn('Not connected to device')
      return
    }

    try {
      let response

      switch (command) {
        case 'setPinState':
          response = await carService.setPinState(params.pin, params.state)
          break
        case 'togglePin':
          response = await carService.togglePin(params.pin)
          break
        case 'resetAllPins':
          response = await carService.resetAllPins()
          break
        case 'getAllPinStates':
          response = await carService.getAllPinStates()
          break
        default:
          console.warn('Unknown GPIO command:', command)
          return
      }

      return response
    } catch (error) {
      console.error('Failed to send GPIO command:', error)
      // If connection fails, disconnect
      if (error.message.includes('connection') || error.message.includes('Failed to send')) {
        disconnectFromDevice()
      }
    }
  }, [isConnected, disconnectFromDevice])

  // Update speed
  const updateSpeed = useCallback(async (speed) => {
    await sendCarCommand('car_speed', speed)
  }, [sendCarCommand])

  // Test connection
  const testConnection = useCallback(async () => {
    if (!isConnected) {
      console.warn('Not connected to device')
      return
    }

    try {
      const response = await carService.testConnection()
      console.log('Connection test successful:', response)
      return response
    } catch (error) {
      console.error('Connection test failed:', error)
      disconnectFromDevice()
    }
  }, [isConnected, disconnectFromDevice])

  // Get device info
  const getDeviceInfo = useCallback(async () => {
    if (!isConnected) {
      console.warn('Not connected to device')
      return
    }

    try {
      const deviceInfo = await carService.getDeviceInfo()
      setDeviceStatus(deviceInfo)
      return deviceInfo
    } catch (error) {
      console.error('Failed to get device info:', error)
      disconnectFromDevice()
    }
  }, [isConnected, disconnectFromDevice])

  // Get device status periodically
  useEffect(() => {
    if (!isConnected) return

    const statusInterval = setInterval(async () => {
      try {
        const status = await carService.getStatus()
        setDeviceStatus(status)
        
        // Update local state from device status
        if (status) {
          setCurrentDirection(status.direction || 'STOP')
          setCurrentSpeed(status.speed || 0)
          setIsMoving(status.isMoving || false)
        }
      } catch (error) {
        console.error('Failed to get status:', error)
        // If status check fails, disconnect
        disconnectFromDevice()
      }
    }, 2000) // Check every 2 seconds

    return () => clearInterval(statusInterval)
  }, [isConnected, disconnectFromDevice])

  return {
    isConnected,
    currentDirection,
    currentSpeed,
    isMoving,
    deviceStatus,
    connectionError,
    connectToDevice,
    disconnectFromDevice,
    sendCarCommand,
    sendGPIOCommand,
    updateSpeed,
    testConnection,
    getDeviceInfo
  }
} 